import { useMemo, useState } from "react";

import { useShowPriceInVat } from "hooks";
import { COMMUNICATION_EVENTS } from "models";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { getInterventionTotals } from "utils/common";

import type { Appointment, Intervention } from "models";
import type { Totals } from "utils/common";

const compareInterventions = (a: Intervention, b: Intervention) => {
  if (a.ordering === b.ordering) return a.id - b.id;
  if (a.ordering == null) return 1;
  if (b.ordering == null) return -1;
  return a.ordering - b.ordering;
};

export const useInterventions = (appointment: Appointment) => {
  const showPriceInVat = useShowPriceInVat();
  const appointmentLocation = useAppointmentLocation();
  const [activeTab, setActiveTab] = useState<null | number>(null);

  const [interventionModal, setInterventionModal] = useState(false);
  const handleRequestClose = () => setInterventionModal(false);

  const handleOpenInterventionModal = () => setInterventionModal(true);

  const diagnoseOverviewEvent = appointment.customer_communication?.events?.find(e => e.type === COMMUNICATION_EVENTS.DiagnoseOverviewOpened);

  const convertedInterventions =
    appointment.interventions
      ?.filter(
        intervention => intervention.check_in_remark_id || intervention.check_in_result_id || intervention.diagnose_overview_remark_id || intervention.question_result_id
      )
      .sort(compareInterventions) ?? [];

  const notConvertedInterventions = appointment.interventions?.filter(intervention => !convertedInterventions.includes(intervention)) ?? [];
  const localAndDmsInterventions = notConvertedInterventions.sort(compareInterventions);

  const interventionTotals: Totals = useMemo(() => {
    if (!appointment.interventions?.length) return { vatGroups: [], totalAmount: 0, approvedTotal: 0 };

    return getInterventionTotals(appointment.interventions, appointmentLocation, showPriceInVat);
  }, [appointment, appointmentLocation, showPriceInVat]);

  return {
    activeTab,
    interventionTotals,
    interventionModal,
    diagnoseOverviewEvent,
    convertedInterventions,
    localAndDmsInterventions,
    setActiveTab,
    handleRequestClose,
    handleOpenInterventionModal
  };
};
